import { Component, OnInit } from '@angular/core';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface Opcao {
  id: number;
  descricao?: string;
  nome?: string;
  codigo?: string;
  natureza?: string;
  tipo?: string;
}

@Component({
  selector: 'app-importar-extrato-banco',
  templateUrl: './importar-extrato-banco.component.html',
  styleUrls: ['./importar-extrato-banco.component.scss']
})
export class ImportarExtratoBancoComponent implements OnInit {
  arquivo: File | null = null;
  senha = '2325';
  nomeAba = 'Banco 1';
  /** Incremental: último dia já importado foi 29/05/2026. */
  dataInicio = '2026-05-30';
  dataFim = '';
  idDocumentoCr = 0;
  idDocumentoCp = 0;
  idFornecedorPadrao = 0;
  idUsuarioPagador = 0;
  marcarPago = true;
  lote = '';
  /** Saldo em 31/12 do ano anterior (positivo = CR, negativo = CP). */
  saldoInicial: number | null = null;
  /** Ano do Fluxo que receberá esse valor como saldo anterior. */
  anoReferenciaSaldo = new Date().getFullYear();
  idPlanoSaldoInicial = 0;

  documentosCr: Opcao[] = [];
  documentosCp: Opcao[] = [];
  fornecedores: Opcao[] = [];
  usuarios: Opcao[] = [];
  planos: Opcao[] = [];

  preview: any = null;
  resultado: any = null;
  comparativo: any = null;
  resultadoZerar: any = null;
  processando = false;
  zerarTudo = true;
  confirmacaoZerar = '';

  constructor(private ajax: AjaxService, private util: UtilService) {}

  ngOnInit(): void {
    this.carregarOpcoes();
  }

  get dataSaldoInicialLabel(): string {
    const ano = Number(this.anoReferenciaSaldo) || new Date().getFullYear();
    return `31/12/${ano - 1}`;
  }

  onArquivoChange(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    this.arquivo = input.files?.[0] || null;
    this.preview = null;
    this.resultado = null;
  }

  carregarOpcoes(): void {
    this.ajax.get(`${API}financeiro/ImportacaoExtratoBanco/Opcoes`).subscribe({
      next: (response: any) => {
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response.message || 'Falha ao carregar opções.');
          return;
        }
        const data = this.desempacotar(response);
        this.documentosCr = this.mapOpcoes(data.documentoscr || data.documentosCr);
        this.documentosCp = this.mapOpcoes(data.documentoscp || data.documentosCp);
        this.fornecedores = this.mapOpcoes(data.fornecedores);
        this.usuarios = this.mapOpcoes(data.usuarios);
        this.planos = this.mapPlanos(data.planos);
        const idLogado = Number(data.idusuariologado || data.idUsuarioLogado || 0);
        if (!this.idUsuarioPagador && idLogado > 0) {
          this.idUsuarioPagador = idLogado;
        } else if (!this.idUsuarioPagador && this.usuarios.length === 1) {
          this.idUsuarioPagador = this.usuarios[0].id;
        }
        if (!this.idPlanoSaldoInicial) {
          const entrada = this.planos.find((p) => (p.natureza || '').toUpperCase().includes('ENTRADA'));
          if (entrada) this.idPlanoSaldoInicial = entrada.id;
        }
      },
      error: () => this.util.toasterShowError('Erro', 'Não foi possível carregar opções da importação.')
    });
  }

  previewImportacao(): void {
    if (!this.arquivo) {
      this.util.toasterShowWarning('Importação', 'Selecione o arquivo Excel ou CSV da aba Banco 1.');
      return;
    }
    this.processando = true;
    this.util.showLoading();
    const fd = this.montarFormData(false);
    this.ajax.postFile(`${API}financeiro/ImportacaoExtratoBanco/Preview`, fd).subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.processando = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Preview', response.message || 'Falha no preview.');
          return;
        }
        this.preview = this.desempacotar(response);
        this.resultado = null;
        this.util.toasterShowSuccess('Preview', `${this.preview?.totallinhas || 0} linha(s) lidas.`);
      },
      error: () => {
        this.util.closeLoading();
        this.processando = false;
        this.util.toasterShowError('Erro', 'Falha ao processar preview.');
      }
    });
  }

  confirmarImportacao(): void {
    if (!this.arquivo) {
      this.util.toasterShowWarning('Importação', 'Selecione o arquivo Excel ou CSV.');
      return;
    }
    if (!this.validarDestino()) return;
    const temSaldo = this.saldoInicial != null && Number(this.saldoInicial) !== 0;
    if (temSaldo && !this.idPlanoSaldoInicial) {
      this.util.toasterShowWarning('Saldo inicial', 'Selecione o plano de contas do saldo inicial.');
      return;
    }
    const extra = temSaldo
      ? `\nTambém será lançado o saldo inicial de ${this.formatMoney(Number(this.saldoInicial))} em ${this.dataSaldoInicialLabel}.`
      : '';
    if (!confirm('Confirmar importação para Contas a Receber e Contas a Pagar? Linhas já importadas (mesmo fingerprint) serão ignoradas.' + extra)) {
      return;
    }
    this.enviarConfirmacao(true);
  }

  /** Lança só o título de saldo inicial (sem arquivo). */
  lancarSaldoInicial(): void {
    if (this.saldoInicial == null || Number(this.saldoInicial) === 0) {
      this.util.toasterShowWarning('Saldo inicial', 'Informe um valor diferente de zero.');
      return;
    }
    if (!this.validarDestino()) return;
    if (!this.idPlanoSaldoInicial) {
      this.util.toasterShowWarning('Saldo inicial', 'Selecione o plano de contas (necessário para o Fluxo de Caixa).');
      return;
    }
    const valor = Number(this.saldoInicial);
    const tipo = valor > 0 ? 'Contas a Receber (crédito)' : 'Contas a Pagar (débito)';
    if (!confirm(
      `Lançar saldo inicial de ${this.formatMoney(Math.abs(valor))} como ${tipo} em ${this.dataSaldoInicialLabel}?\n` +
      `Esse título entra no Fluxo de Caixa de ${this.anoReferenciaSaldo} como saldo anterior.`
    )) {
      return;
    }
    this.enviarConfirmacao(false);
  }

  private validarDestino(): boolean {
    if (!this.idDocumentoCr || !this.idDocumentoCp || !this.idFornecedorPadrao) {
      this.util.toasterShowWarning('Importação', 'Selecione documentos CR/CP e fornecedor padrão.');
      return false;
    }
    if (!this.idUsuarioPagador) {
      this.util.toasterShowWarning('Importação', 'Selecione o usuário pagador (Contas a Receber).');
      return false;
    }
    return true;
  }

  private enviarConfirmacao(exigeArquivo: boolean): void {
    if (exigeArquivo && !this.arquivo) {
      this.util.toasterShowWarning('Importação', 'Selecione o arquivo Excel ou CSV.');
      return;
    }
    this.processando = true;
    this.util.showLoading();
    const fd = this.montarFormData(true, !exigeArquivo);
    this.ajax.postFile(`${API}financeiro/ImportacaoExtratoBanco/Confirmar`, fd).subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.processando = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Importação', response.message || 'Falha na importação.');
          return;
        }
        this.resultado = this.desempacotar(response);
        this.util.toasterShowSuccess('Importação', this.resultado?.mensagem || 'Concluída.');
        this.carregarComparativo();
      },
      error: () => {
        this.util.closeLoading();
        this.processando = false;
        this.util.toasterShowError('Erro', 'Falha ao confirmar importação.');
      }
    });
  }

  carregarComparativo(): void {
    let url = `${API}financeiro/ImportacaoExtratoBanco/Comparar?`;
    if (this.dataInicio) url += `dataInicio=${encodeURIComponent(this.dataInicio)}&`;
    if (this.dataFim) url += `dataFim=${encodeURIComponent(this.dataFim)}`;
    this.ajax.get(url).subscribe({
      next: (response: any) => {
        if (response?.hasErrors) return;
        this.comparativo = this.desempacotar(response);
      }
    });
  }

  zerarContas(): void {
    const texto = (this.confirmacaoZerar || '').trim().toUpperCase();
    if (texto !== 'ZERAR') {
      this.util.toasterShowWarning('Limpeza', 'Digite ZERAR no campo de confirmação para continuar.');
      return;
    }

    const escopo = this.zerarTudo
      ? 'TODOS os Contas a Receber e Contas a Pagar ativos desta empresa'
      : 'somente os lançamentos importados da planilha Banco 1 ([IMPORT-BANCO1])';

    if (!confirm(`ATENÇÃO: isso vai inativar ${escopo}.\n\nDeseja continuar?`)) {
      return;
    }
    if (!confirm('Confirma definitivamente? Depois você poderá importar a planilha novamente.')) {
      return;
    }

    this.processando = true;
    this.util.showLoading();
    this.ajax.post(`${API}financeiro/ImportacaoExtratoBanco/ZerarContas`, {
      confirmar: true,
      confirmacaoTexto: 'ZERAR',
      zerarTudo: this.zerarTudo
    }).subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.processando = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Limpeza', response.message || 'Falha ao zerar contas.');
          return;
        }
        this.resultadoZerar = this.desempacotar(response);
        this.resultado = null;
        this.comparativo = null;
        this.confirmacaoZerar = '';
        this.util.toasterShowSuccess('Limpeza', this.resultadoZerar?.mensagem || 'Contas inativadas.');
        this.carregarComparativo();
      },
      error: () => {
        this.util.closeLoading();
        this.processando = false;
        this.util.toasterShowError('Erro', 'Falha ao zerar contas.');
      }
    });
  }

  formatMoney(v: number): string {
    return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  /**
   * @param confirmar inclui campos de destino
   * @param semArquivo permite FormData sem arquivo (só saldo inicial)
   */
  private montarFormData(confirmar: boolean, semArquivo = false): FormData {
    const fd = new FormData();
    if (!semArquivo && this.arquivo) {
      fd.append('arquivo', this.arquivo as File, this.arquivo.name);
    }
    if (this.senha) fd.append('senha', this.senha);
    if (this.nomeAba) fd.append('nomeAba', this.nomeAba);
    if (this.dataInicio) fd.append('dataInicio', this.dataInicio);
    if (this.dataFim) fd.append('dataFim', this.dataFim);
    if (confirmar) {
      fd.append('idDocumentoCr', String(this.idDocumentoCr));
      fd.append('idDocumentoCp', String(this.idDocumentoCp));
      fd.append('idFornecedorPadrao', String(this.idFornecedorPadrao));
      fd.append('idUsuarioPagador', String(this.idUsuarioPagador));
      fd.append('marcarPago', String(this.marcarPago));
      if (this.lote) fd.append('lote', this.lote);
      if (this.saldoInicial != null && Number(this.saldoInicial) !== 0) {
        fd.append('saldoInicial', String(this.saldoInicial).replace(',', '.'));
        fd.append('anoReferenciaSaldo', String(this.anoReferenciaSaldo || new Date().getFullYear()));
        if (this.idPlanoSaldoInicial) {
          fd.append('idPlanoSaldoInicial', String(this.idPlanoSaldoInicial));
        }
      }
    }
    return fd;
  }

  private mapOpcoes(lista: any): Opcao[] {
    if (!Array.isArray(lista)) return [];
    return lista.map((x: any) => ({
      id: Number(x.id || 0),
      descricao: String(x.descricao || x.nome || ''),
      nome: String(x.nome || x.descricao || '')
    })).filter((x: Opcao) => x.id > 0);
  }

  private mapPlanos(lista: any): Opcao[] {
    if (!Array.isArray(lista)) return [];
    return lista.map((x: any) => {
      const codigo = String(x.codigo || '');
      const nome = String(x.nome || x.descricao || '');
      const natureza = String(x.natureza || '');
      const tipo = String(x.tipo || '');
      return {
        id: Number(x.id || 0),
        codigo,
        nome,
        natureza,
        tipo,
        descricao: `${codigo} — ${nome}${natureza ? ' (' + natureza + ')' : ''}`
      } as Opcao;
    }).filter((x: Opcao) => x.id > 0);
  }

  private desempacotar(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else break;
    }
    return this.util.converterParaLowercase(atual ?? {});
  }
}
